import { ArrowRight, LucideIcon } from 'lucide-react';
import { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';

interface GlassButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  /** Show arrow icon on the right (default: true) */
  showArrow?: boolean;
  /** Custom icon instead of arrow */
  icon?: LucideIcon;
  /** Variant: 'light' (default) | 'dark' | 'simple' | 'accent' | 'accent-simple' */
  variant?: 'light' | 'dark' | 'simple' | 'accent' | 'accent-simple';
  /** Size: 'sm' | 'md' (default) | 'lg' */
  size?: 'sm' | 'md' | 'lg';
}

const GlassButton = forwardRef<HTMLButtonElement, GlassButtonProps>(
  (
    {
      children,
      showArrow = true,
      icon: Icon,
      variant = 'light',
      size = 'md',
      className = '',
      ...props
    },
    ref
  ) => {
    // Size classes
    const isSimple = variant === 'simple' || variant === 'accent-simple';
    const sizeClasses = {
      sm: isSimple ? 'px-4 py-2 text-xs' : 'pl-4 pr-1.5 py-1.5 text-xs',
      md: isSimple ? 'px-6 py-3 text-sm' : 'pl-6 pr-2 py-2 text-sm',
      lg: isSimple ? 'px-8 py-4 text-base' : 'pl-8 pr-2.5 py-2.5 text-base',
    };

    const iconSizes = {
      sm: 'w-6 h-6',
      md: 'w-8 h-8',
      lg: 'w-10 h-10',
    };

    const arrowSizes = {
      sm: 'w-3 h-3',
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
    };

    // Variant classes
    const variantClasses = {
      light: 'btn-glass',
      dark: 'btn-glass-dark',
      simple: 'btn-glass-simple',
      accent: 'btn-glass-accent',
      'accent-simple': 'btn-glass-accent-simple',
    };

    const IconComponent = Icon || ArrowRight;
    const shouldShowIcon = showArrow || Icon;
    const isSimpleVariant = variant === 'simple' || variant === 'accent-simple';

    if (isSimpleVariant) {
      return (
        <button
          ref={ref}
          className={`${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
          {...props}
        >
          {children}
          {shouldShowIcon && (
            <IconComponent
              className={`${arrowSizes[size]} transition-transform group-hover:translate-x-0.5`}
            />
          )}
        </button>
      );
    }

    return (
      <button
        ref={ref}
        className={`${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
        {...props}
      >
        <span>{children}</span>
        {shouldShowIcon && (
          <span className={`btn-glass-icon ${iconSizes[size]}`}>
            <IconComponent className={arrowSizes[size]} />
          </span>
        )}
      </button>
    );
  }
);

GlassButton.displayName = 'GlassButton';

export default GlassButton;

